import { useCallback, useState, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { TextField, Button, Stack, CircularProgress, Typography } from '@mui/material';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { openTaskModal as openTaskModalState } from '../state/atoms';
import {
  riskAssessmentAssemblyTemplate,
  selectedAssemblyId as selectedAssemblyIdState,
  selectedAssemblyIds as selectedIds,
  unselectedAssemblyIds,
  selectedRiskAssessmentFilters,
} from '@/components/Analysis/state';
import {
  useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation,
  GetAssembliesByRiskAssessmentFiltersDocument,
  GetVersionsDocument,
  useGetAssemblyForTaskModalQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { BasicTabs } from '@/components/shared/BasicTabs';
import {
  closeIconStyles,
  paperStyles,
  taskItemsTabStyles,
  taskTabStyles,
  textBoxStyles,
  titleStyles,
  ModalContent,
} from './styles';

type TaskKey = 'risk_task_description' | 'risk_task_frequency' | 'risk_task_responsibility';

type TaskItems = [string, string, string, string];

type TaskDataType = Record<TaskKey, TaskItems>;

const TASK_TABS: { label: string; key: TaskKey }[] = [
  { label: 'Task Description', key: 'risk_task_description' },
  { label: 'Task Frequency', key: 'risk_task_frequency' },
  { label: 'Task Responsibility', key: 'risk_task_responsibility' },
];

const EMPTY_ITEMS: TaskItems = ['', '', '', ''];

const parseItems = (value: string): TaskItems => {
  const items = value
    .split(/\n(?=\d+\)\s)/)
    .map((block) => block.replace(/^\d+\)\s*/, '').trim())
    .filter((block) => block.length > 0);
  return [items[0] ?? '', items[1] ?? '', items[2] ?? '', items[3] ?? ''];
};

const serializeItems = (items: TaskItems): string =>
  items
    .filter((item) => item.trim().length > 0)
    .map((item, index) => `${index + 1}) ${item}`)
    .join('\n');

const emptyTaskData = (): TaskDataType => ({
  risk_task_description: [...EMPTY_ITEMS],
  risk_task_frequency: [...EMPTY_ITEMS],
  risk_task_responsibility: [...EMPTY_ITEMS],
});

type TaskModalProps = {
  updateAllAssemblies: boolean;
};

export const TaskModal = ({ updateAllAssemblies }: TaskModalProps) => {
  const structureId = useGetStructureId();
  const [openTaskModal, setOpenTaskModal] = useAtom(openTaskModalState);
  const selectedAssemblyId = useAtomValue(selectedAssemblyIdState);
  const selectedAssemblyIds = useAtomValue(selectedIds);
  const excludedAssemblyIds = useAtomValue(unselectedAssemblyIds);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedTaskTypeTab, setSelectedTaskTypeTab] = useState(0);
  const [selectedItemTabs, setSelectedItemTabs] = useState<Record<TaskKey, number>>({
    risk_task_description: 0,
    risk_task_frequency: 0,
    risk_task_responsibility: 0,
  });

  const [taskData, setTaskData] = useState<TaskDataType>(emptyTaskData());
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleClose = useCallback(() => {
    setOpenTaskModal(false);
    setSelectedTaskTypeTab(0);
  }, [setOpenTaskModal]);

  const { data: assemblyTemplateFieldData, loading: taskDataLoading } =
    useGetAssemblyForTaskModalQuery({
      variables: {
        assemblyId: selectedAssemblyId ?? '',
      },
      skip: !selectedAssemblyId,
    });

  useEffect(() => {
    if (assemblyTemplateFieldData?.assembly) {
      const { templateFieldData } = assemblyTemplateFieldData.assembly;
      setTaskData({
        risk_task_description: parseItems(templateFieldData?.risk_task_description ?? ''),
        risk_task_frequency: parseItems(templateFieldData?.risk_task_frequency ?? ''),
        risk_task_responsibility: parseItems(templateFieldData?.risk_task_responsibility ?? ''),
      });
    }
  }, [assemblyTemplateFieldData?.assembly]);

  const [updateTaskData, { loading: updatingTaskData }] =
    useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation({
      refetchQueries: [GetAssembliesByRiskAssessmentFiltersDocument, GetVersionsDocument],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackBar('The task description has been saved successfully', 'success');
        setOpenConfirmationDialog(false);
        setIsSaveDisabled(true);
      },
    });

  const handleSave = useCallback(() => {
    setOpenTaskModal(false);
    setOpenConfirmationDialog(true);
  }, [setOpenTaskModal]);

  const buildSelectedInputs = useCallback(() => {
    const inputs: { id: string; value: string }[] = [];
    for (const [key, items] of Object.entries(taskData) as [TaskKey, TaskItems][]) {
      inputs.push({ id: key, value: serializeItems(items) });
      for (const [index, item] of items.entries()) {
        inputs.push({ id: `${key}_${index + 1}`, value: item });
      }
    }
    return inputs;
  }, [taskData]);

  const handleUpdateSelectedComponent = useCallback(() => {
    updateTaskData({
      variables: {
        input: {
          structureId,
          templateId: template?.id ?? '',
          selectedFilters,
          assemblyIds: selectedAssemblyId ? [selectedAssemblyId] : [],
          excludedAssemblyIds: [],
          selectedInputs: buildSelectedInputs(),
          updateAllAssemblies: false,
          lastModifiedFor: 'Task Description',
        },
      },
    });
  }, [
    buildSelectedInputs,
    selectedAssemblyId,
    selectedFilters,
    structureId,
    template?.id,
    updateTaskData,
  ]);

  const handleUpdateAllSelected = useCallback(() => {
    updateTaskData({
      variables: {
        input: {
          structureId,
          templateId: template?.id ?? '',
          selectedFilters,
          assemblyIds: selectedAssemblyIds,
          excludedAssemblyIds: excludedAssemblyIds,
          selectedInputs: buildSelectedInputs(),
          updateAllAssemblies: updateAllAssemblies,
          lastModifiedFor: 'Task Description',
        },
      },
    });
  }, [
    buildSelectedInputs,
    excludedAssemblyIds,
    selectedAssemblyIds,
    selectedFilters,
    structureId,
    template?.id,
    updateAllAssemblies,
    updateTaskData,
  ]);

  const handleCloseConfirmationDialog = useCallback(() => {
    setOpenConfirmationDialog(false);
    setOpenTaskModal(true);
  }, [setOpenTaskModal]);

  const handleTaskTypeTabChange = useCallback((newIndex: number) => {
    setSelectedTaskTypeTab(newIndex);
  }, []);

  const handleItemTabChange = useCallback(
    (taskKey: TaskKey) => (newIndex: number) => {
      setSelectedItemTabs((previous) => ({ ...previous, [taskKey]: newIndex }));
    },
    []
  );

  const handleItemChange = useCallback((key: TaskKey, itemIndex: number, value: string) => {
    setIsSaveDisabled(false);
    setTaskData((previousData) => {
      const updated: TaskItems = [...previousData[key]];
      updated[itemIndex] = value;
      return { ...previousData, [key]: updated };
    });
  }, []);

  const taskTypeTabs = useMemo(
    () =>
      TASK_TABS.map((tab, typeIndex) => ({
        label: tab.label,
        index: typeIndex,
        renderingStrategy: 'eager',
        component: (
          <BasicTabs
            tabs={[1, 2, 3, 4].map((itemNumber, itemIndex) => ({
              label: `${tab.label} ${itemNumber}`,
              index: itemIndex,
              renderingStrategy: 'eager',
              component: (
                <TextField
                  autoFocus={itemIndex === 0}
                  type="text"
                  fullWidth
                  multiline
                  value={taskData[tab.key][itemIndex]}
                  onChange={(event) => handleItemChange(tab.key, itemIndex, event.target.value)}
                  disabled={taskDataLoading}
                  sx={textBoxStyles}
                />
              ),
            }))}
            selectedTab={selectedItemTabs[tab.key]}
            changeTab={handleItemTabChange(tab.key)}
            fullWidth={false}
            tabsStyles={taskItemsTabStyles}
            customSeperator={<></>}
          />
        ),
      })),
    [taskData, taskDataLoading, selectedItemTabs, handleItemChange, handleItemTabChange]
  );

  return (
    <>
      <GenericDialog
        open={openTaskModal}
        onClose={handleClose}
        title="Task"
        titleStyles={titleStyles}
        showCloseIcon
        closeIconStyles={closeIconStyles}
        paperStyles={paperStyles}
        contentStyles={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        actionsStyles={{
          padding: '1.6rem 3.2rem',
          borderTop: `1px solid ${abyssColors.primary[100]}`,
        }}
        content={
          <ModalContent>
            <BasicTabs
              tabs={taskTypeTabs}
              selectedTab={selectedTaskTypeTab}
              changeTab={handleTaskTypeTabChange}
              tabsStyles={taskTabStyles}
              fullWidth={false}
            />
          </ModalContent>
        }
        actions={
          <Stack direction="row" justifyContent="flex-end" width="100%" gap={2}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={updatingTaskData || taskDataLoading || isSaveDisabled}
              onClick={handleSave}
            >
              {updatingTaskData ? (
                <>
                  <CircularProgress sx={{ color: '#fff' }} size="2.4rem" />
                  <span style={{ marginLeft: '10px' }}>Saving</span>
                </>
              ) : (
                <> Save </>
              )}
            </Button>
          </Stack>
        }
      />
      <GenericDialog
        open={openConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        title="Updating Task Details"
        titleStyles={titleStyles}
        showCloseIcon
        closeIconStyles={closeIconStyles}
        actionsStyles={{
          padding: '1.6rem 3.2rem',
          borderTop: `1px solid ${abyssColors.primary[100]}`,
        }}
        content={
          <Typography
            fontSize="1.4rem"
            color={abyssColors.primary[700]}
            lineHeight={1.43}
            letterSpacing="normal"
          >
            You have updated the task description. Do you want to apply these changes to only this
            component or to all selected components?
          </Typography>
        }
        actions={
          <Stack direction="row" justifyContent="flex-end" width="100%" gap={2}>
            <Button sx={textButtonStyle} onClick={handleUpdateAllSelected}>
              Update All Selected
            </Button>
            <Button
              variant="contained"
              disabled={updatingTaskData || taskDataLoading || isSaveDisabled}
              sx={containedButtonStyle}
              onClick={handleUpdateSelectedComponent}
            >
              Update This Component
            </Button>
          </Stack>
        }
      />
    </>
  );
};
